'use client';

import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Song {
  id: string;
  title: string;
  artist: string;
  album: string;
  genre: string;
  duration: string;
  bpm: number;
  keySig: string;
  trackStatus: 'ready' | 'playing' | 'processing' | 'archived';
  plays: number;
  addedAt: string;
  fileSize: string;
  fileUrl: string;
  albumArtUrl: string;
}

export interface ScheduleBlock {
  id: string;
  day: number;
  startHour: number;
  durationHours: number;
  showName: string;
  description: string;
  songIds: string[];
  repeat: string;
  autoAdvance: boolean;
  status: 'scheduled' | 'live' | 'completed';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    const patterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return patterns.some((p) => p.test(error.message));
  }
  return false;
}

function rowToSong(row: any): Song {
  return {
    id: row.id,
    title: row.title,
    artist: row.artist,
    album: row.album,
    genre: row.genre,
    duration: row.duration,
    bpm: row.bpm,
    keySig: row.key_sig,
    trackStatus: row.track_status,
    plays: row.plays,
    addedAt: row.added_at,
    fileSize: row.file_size,
    fileUrl: row.file_url ?? '',
    albumArtUrl: row.album_art_url ?? '',
  };
}

function rowToBlock(row: any): ScheduleBlock {
  return {
    id: row.id,
    day: row.day_of_week,
    startHour: row.start_hour,
    durationHours: parseFloat(row.duration_hours),
    showName: row.show_name,
    description: row.description,
    songIds: row.song_ids ?? [],
    repeat: row.repeat_rule,
    autoAdvance: row.auto_advance,
    status: row.block_status,
  };
}

// ─── Song Service ─────────────────────────────────────────────────────────────

export const songService = {
  async getAll(): Promise<Song[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('songs')
        .select('*')
        .order('added_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Songs fetch error:', error.message);
        return [];
      }
      return (data ?? []).map(rowToSong);
    } catch (err: any) {
      console.log('Songs service error:', err.message);
      throw err;
    }
  },

  async upsert(song: Song): Promise<Song | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('songs')
        .upsert({
          id: song.id,
          title: song.title,
          artist: song.artist,
          album: song.album,
          genre: song.genre,
          duration: song.duration,
          bpm: song.bpm,
          key_sig: song.keySig,
          track_status: song.trackStatus,
          plays: song.plays,
          added_at: song.addedAt,
          file_size: song.fileSize,
          file_url: song.fileUrl,
          album_art_url: song.albumArtUrl ?? '',
          updated_at: new Date().toISOString(),
        }, { onConflict: 'id' })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Song upsert error:', error.message);
        return null;
      }
      return data ? rowToSong(data) : null;
    } catch (err: any) {
      console.log('Song upsert service error:', err.message);
      throw err;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('songs').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Song delete error:', error.message);
        return false;
      }
      return true;
    } catch (err: any) {
      console.log('Song delete service error:', err.message);
      throw err;
    }
  },

  async incrementPlays(id: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase.rpc('increment_song_plays' as any, { song_id: id }).catch(() => {
        // fallback: just update directly
        supabase.from('songs').select('plays').eq('id', id).single().then(({ data }) => {
          if (data) {
            supabase.from('songs').update({ plays: (data.plays ?? 0) + 1 }).eq('id', id);
          }
        });
      });
    } catch {
      // non-critical
    }
  },
};

// ─── Schedule Service ─────────────────────────────────────────────────────────

export const scheduleService = {
  async getAll(): Promise<ScheduleBlock[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('schedule_blocks')
        .select('*')
        .order('day_of_week', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Schedule fetch error:', error.message);
        return [];
      }
      return (data ?? []).map(rowToBlock);
    } catch (err: any) {
      console.log('Schedule service error:', err.message);
      throw err;
    }
  },

  async upsert(block: ScheduleBlock): Promise<ScheduleBlock | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('schedule_blocks')
        .upsert({
          id: block.id,
          day_of_week: block.day,
          start_hour: block.startHour,
          duration_hours: block.durationHours,
          show_name: block.showName,
          description: block.description,
          song_ids: block.songIds,
          repeat_rule: block.repeat,
          auto_advance: block.autoAdvance,
          block_status: block.status,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'id' })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Block upsert error:', error.message);
        return null;
      }
      return data ? rowToBlock(data) : null;
    } catch (err: any) {
      console.log('Block upsert service error:', err.message);
      throw err;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('schedule_blocks').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Block delete error:', error.message);
        return false;
      }
      return true;
    } catch (err: any) {
      console.log('Block delete service error:', err.message);
      throw err;
    }
  },
};

// ─── Listener Session Service ─────────────────────────────────────────────────

export interface ListenerSession {
  id: string;
  joinedAt: string;
  lastHeartbeat: string;
  isActive: boolean;
}

export interface HourlyListenerData {
  time: string;
  listeners: number;
}

export const listenerService = {
  async joinSession(): Promise<string | null> {
    const supabase = createClient();
    try {
      const sessionId = crypto.randomUUID();
      const { error } = await supabase.from('listener_sessions').insert({
        id: sessionId,
        joined_at: new Date().toISOString(),
        last_heartbeat: new Date().toISOString(),
        is_active: true,
      });
      if (error) {
        console.log('Listener join error:', error.message);
        return null;
      }
      return sessionId;
    } catch (err: any) {
      console.log('Listener join service error:', err.message);
      return null;
    }
  },

  async heartbeat(sessionId: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase
        .from('listener_sessions')
        .update({ last_heartbeat: new Date().toISOString() })
        .eq('id', sessionId);
    } catch {
      // non-critical
    }
  },

  async leaveSession(sessionId: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase
        .from('listener_sessions')
        .update({ is_active: false })
        .eq('id', sessionId);
    } catch {
      // non-critical
    }
  },

  async getLiveCount(): Promise<number> {
    const supabase = createClient();
    try {
      // Count sessions with a heartbeat in the last 5 minutes (truly live)
      const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
      const { count: liveCount, error: liveError } = await supabase
        .from('listener_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true)
        .gte('last_heartbeat', fiveMinAgo);

      if (!liveError && liveCount !== null && liveCount > 0) {
        return liveCount;
      }

      // Fallback: count sessions joined in the last 30 minutes that are still marked active
      const thirtyMinAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();
      const { count: recentCount, error: recentError } = await supabase
        .from('listener_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true)
        .gte('joined_at', thirtyMinAgo);

      if (!recentError) return recentCount ?? 0;
      return 0;
    } catch {
      return 0;
    }
  },

  async getHourlyTrend(): Promise<HourlyListenerData[]> {
    const supabase = createClient();
    try {
      // Fetch all sessions from the last 24 hours
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('listener_sessions')
        .select('joined_at, last_heartbeat, is_active')
        .gte('joined_at', since)
        .order('joined_at', { ascending: true });

      if (error || !data) return [];

      // Build 24-hour buckets — count sessions active during each hour window
      const now = new Date();
      const buckets: { key: string; start: Date; end: Date; count: number }[] = [];
      for (let i = 23; i >= 0; i--) {
        const start = new Date(now);
        start.setHours(now.getHours() - i, 0, 0, 0);
        const end = new Date(start);
        end.setHours(start.getHours() + 1, 0, 0, 0);
        buckets.push({
          key: `${String(start.getHours()).padStart(2, '0')}:00`,
          start,
          end,
          count: 0,
        });
      }

      // A session is "active" during an hour if:
      //   joined_at < hour_end  AND  last_heartbeat >= hour_start
      data.forEach((row) => {
        const joined = new Date(row.joined_at);
        const heartbeat = new Date(row.last_heartbeat);
        buckets.forEach((bucket) => {
          if (joined < bucket.end && heartbeat >= bucket.start) {
            bucket.count += 1;
          }
        });
      });

      return buckets.map(({ key, count }) => ({ time: key, listeners: count }));
    } catch {
      return [];
    }
  },

  subscribeToCount(callback: (count: number) => void) {
    const supabase = createClient();
    const channelName = `listener_sessions_count_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes' as any,
        { event: '*', schema: 'public', table: 'listener_sessions' },
        async () => {
          const count = await listenerService.getLiveCount();
          callback(count);
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  },
};

// ─── Station Settings Service ─────────────────────────────────────────────────

export interface StationSettings {
  id?: string;
  stationName: string;
  description: string;
  logoUrl: string;
  contactEmail: string;
  streamBitrate: string;
  broadcastStartHour: number;
  broadcastEndHour: number;
}

function rowToSettings(row: any): StationSettings {
  return {
    id: row.id,
    stationName: row.station_name,
    description: row.description,
    logoUrl: row.logo_url ?? '',
    contactEmail: row.contact_email ?? '',
    streamBitrate: row.stream_bitrate,
    broadcastStartHour: row.broadcast_start_hour,
    broadcastEndHour: row.broadcast_end_hour,
  };
}

export const stationSettingsService = {
  async get(): Promise<StationSettings | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('station_settings')
        .select('*')
        .limit(1)
        .single();
      if (error) {
        console.log('Station settings fetch error:', error.message);
        return null;
      }
      return data ? rowToSettings(data) : null;
    } catch (err: any) {
      console.log('Station settings service error:', err.message);
      return null;
    }
  },

  async save(settings: StationSettings): Promise<StationSettings | null> {
    const supabase = createClient();
    try {
      const payload = {
        station_name: settings.stationName,
        description: settings.description,
        logo_url: settings.logoUrl,
        contact_email: settings.contactEmail,
        stream_bitrate: settings.streamBitrate,
        broadcast_start_hour: settings.broadcastStartHour,
        broadcast_end_hour: settings.broadcastEndHour,
        updated_at: new Date().toISOString(),
      };

      if (settings.id) {
        const { data, error } = await supabase
          .from('station_settings')
          .update(payload)
          .eq('id', settings.id)
          .select()
          .single();
        if (error) {
          console.log('Station settings update error:', error.message);
          return null;
        }
        return data ? rowToSettings(data) : null;
      } else {
        const { data, error } = await supabase
          .from('station_settings')
          .insert(payload)
          .select()
          .single();
        if (error) {
          console.log('Station settings insert error:', error.message);
          return null;
        }
        return data ? rowToSettings(data) : null;
      }
    } catch (err: any) {
      console.log('Station settings save error:', err.message);
      return null;
    }
  },

  async uploadLogo(file: File): Promise<string | null> {
    const supabase = createClient();
    try {
      const ext = file.name.split('.').pop();
      const fileName = `station-logo-${Date.now()}.${ext}`;
      const { data, error } = await supabase.storage
        .from('album-art')
        .upload(`logos/${fileName}`, file, { upsert: true, contentType: file.type });
      if (error) {
        console.log('Logo upload error:', error.message);
        return null;
      }
      const { data: urlData } = supabase.storage
        .from('album-art')
        .getPublicUrl(data.path);
      return urlData.publicUrl;
    } catch (err: any) {
      console.log('Logo upload service error:', err.message);
      return null;
    }
  },
};
